import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, Download, Filter, FileDown } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { api, API } from "@/lib/api";
import { toast } from "sonner";

const STATUS_COLOR = {
  new: "bg-stone text-forest",
  in_review: "bg-warning/15 text-warning",
  approved: "bg-success/15 text-success",
  rejected: "bg-danger/15 text-danger",
};
const STATUS_LABEL = { new: "New", in_review: "In Review", approved: "Approved", rejected: "Rejected" };

export default function ClientsList() {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [type, setType] = useState("all");
  const [status, setStatus] = useState("all");
  const [selected, setSelected] = useState(new Set());
  const [pdfLoading, setPdfLoading] = useState(false);

  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleAll = (checked) => {
    setSelected(checked ? new Set(clients.map((c) => c.id)) : new Set());
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (type !== "all") params.insurance_type = type;
      if (status !== "all") params.status = status;
      const { data } = await api.get("/clients", { params });
      setClients(data);
    } catch {
      toast.error("Could not load clients");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [search, type, status]);

  const exportCsv = async () => {
    try {
      const token = localStorage.getItem("if_token");
      const resp = await fetch(`${API}/clients/export`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error("Export failed");
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `clients-${Date.now()}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Export downloaded");
    } catch {
      toast.error("Export failed");
    }
  };

  const downloadBulkPdf = async () => {
    setPdfLoading(true);
    try {
      const token = localStorage.getItem("if_token");
      const ids = Array.from(selected);
      const resp = await fetch(`${API}/clients/pdf-bulk`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ client_ids: ids }),
      });
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        throw new Error(data.detail || "PDF generation failed");
      }
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `clients-${ids.length || "all"}-${Date.now()}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`PDF downloaded (${ids.length || clients.length} client${(ids.length || clients.length) === 1 ? "" : "s"})`);
    } catch (err) {
      toast.error(err.message || "PDF generation failed");
    } finally {
      setPdfLoading(false);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return "—";
    return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  };

  const empty = !loading && clients.length === 0;

  return (
    <div className="p-6 lg:p-10">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-clay font-semibold mb-2">Clients</div>
          <h1 className="font-heading text-3xl md:text-4xl tracking-tight font-medium">All applications</h1>
          <p className="text-sage mt-2 text-sm">Search, filter and review every client submission.</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Button
            onClick={downloadBulkPdf}
            disabled={pdfLoading || (clients.length === 0 && !loading)}
            variant="outline"
            className="border-clay text-clay hover:bg-clay hover:text-bone rounded-sm h-10"
            data-testid="bulk-pdf-btn"
          >
            <FileDown className="w-4 h-4 mr-2" />
            {pdfLoading
              ? "Generating…"
              : selected.size > 0
              ? `Download PDF (${selected.size})`
              : "Download all as PDF"}
          </Button>
          <Button
            onClick={exportCsv}
            variant="outline"
            className="border-forest text-forest hover:bg-forest hover:text-bone rounded-sm h-10"
            data-testid="export-csv-btn"
          >
            <Download className="w-4 h-4 mr-2" /> Export CSV
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-sage" />
          <Input
            placeholder="Search by name, email, phone or ID…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-11 pl-10 bg-white border-line input-clean"
            data-testid="clients-search-input"
          />
        </div>
        <Select value={type} onValueChange={setType}>
          <SelectTrigger className="md:w-48 h-11 bg-white border-line" data-testid="filter-type-select">
            <Filter className="w-4 h-4 mr-2 text-sage" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            <SelectItem value="health">Health</SelectItem>
            <SelectItem value="life">Life</SelectItem>
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="md:w-48 h-11 bg-white border-line" data-testid="filter-status-select">
            <Filter className="w-4 h-4 mr-2 text-sage" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="new">New</SelectItem>
            <SelectItem value="in_review">In Review</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="bg-white border border-line rounded-sm overflow-hidden">
        <Table data-testid="clients-table">
          <TableHeader>
            <TableRow className="bg-cream hover:bg-cream">
              <TableHead className="w-10">
                <Checkbox
                  checked={clients.length > 0 && selected.size === clients.length}
                  onCheckedChange={toggleAll}
                  data-testid="select-all-checkbox"
                  aria-label="Select all"
                />
              </TableHead>
              <TableHead className="text-forest font-medium">Name</TableHead>
              <TableHead className="text-forest font-medium">Type</TableHead>
              <TableHead className="text-forest font-medium">Contact</TableHead>
              <TableHead className="text-forest font-medium">Submitted</TableHead>
              <TableHead className="text-forest font-medium">Status</TableHead>
              <TableHead className="text-right text-forest font-medium">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-sage py-10">Loading…</TableCell>
              </TableRow>
            )}
            {empty && (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-sage py-12">
                  No clients match your filters yet.
                </TableCell>
              </TableRow>
            )}
            {!loading &&
              clients.map((c) => (
                <TableRow
                  key={c.id}
                  className="hover:bg-cream/50 cursor-pointer"
                  onClick={() => navigate(`/agent/clients/${c.id}`)}
                  data-testid={`client-row-${c.id}`}
                >
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      checked={selected.has(c.id)}
                      onCheckedChange={() => toggleOne(c.id)}
                      data-testid={`select-client-${c.id}`}
                      aria-label={`Select ${c.full_name}`}
                    />
                  </TableCell>
                  <TableCell>
                    <div className="font-medium text-forest">{c.full_name}</div>
                    <div className="text-xs text-sage">ID #{c.id_number}</div>
                  </TableCell>
                  <TableCell>
                    <span className="capitalize text-sm">{c.insurance_type}</span>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm text-forest">{c.email}</div>
                    <div className="text-xs text-sage">{c.phone}</div>
                  </TableCell>
                  <TableCell className="text-sm text-sage">{formatDate(c.created_at)}</TableCell>
                  <TableCell>
                    <span className={`text-xs px-2 py-1 rounded-sm ${STATUS_COLOR[c.status]}`}>
                      {STATUS_LABEL[c.status]}
                    </span>
                  </TableCell>
                  <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                    <Link
                      to={`/agent/clients/${c.id}`}
                      className="text-clay hover:text-claydark text-sm font-medium transition-colors duration-200"
                      data-testid={`view-client-${c.id}`}
                    >
                      Open →
                    </Link>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
